/* eslint-disable react-refresh/only-export-components -- the context, its hook and the wrapper that provides it belong together */
import { createContext, useContext, useMemo, type CSSProperties, type ReactNode } from "react";
import { moduleAccentScope, type ModuleAccent, type ModuleAccentScope } from "@/lib/module-accents";

/**
 * The module accent scope of the page being shown, as a value that travels with the React tree.
 *
 * `--module-color`, `--module-tint`, `--module-border` and `--module-foreground` are CSS custom
 * properties set on an element (`[data-module-scope]` in src/index.css), so they reach only that
 * element's DOM descendants. A Radix or vaul portal renders into `document.body`, outside the
 * element AppLayout scopes, and its content fell back to neutral colours (#7912): the phone Filters
 * sheet drew its chips grey. Portals keep React context, so the overlay primitives in this folder
 * read this and put the scope on their own content (`useModuleScopeProps`).
 *
 * `null` outside the next chassis, and outside AppLayout: AppLayout provides it only under
 * ui_uplift = next, so a classic page's overlays are exactly what they were.
 */
export const ModuleScopeContext = createContext<ModuleAccentScope | null>(null);

type ScopeProps = { "data-module-scope"?: string; "data-ui"?: "next"; style?: CSSProperties };

const NO_SCOPE: ScopeProps = {};

/**
 * What a portaled overlay's content element spreads after its own props (it is the content that
 * carries the scope, as the sheet's own element is not inside the page's):
 *
 *     const moduleScope = useModuleScopeProps(props.style);
 *     <Primitive.Content {...props} {...moduleScope} />
 *
 * Nothing when no scope is provided, so a classic page's overlay gets no new attribute or style.
 * `data-ui="next"` goes with it because index.css gives the dark module values only under the next
 * chassis, and a portal is outside the element that says so. The caller's `style` is kept.
 */
export function useModuleScopeProps(style?: CSSProperties): ScopeProps {
  const scope = useContext(ModuleScopeContext);
  return useMemo(
    () => (scope === null ? NO_SCOPE : { "data-module-scope": scope["data-module-scope"], "data-ui": "next", style: { ...scope.style, ...style } }),
    [scope, style],
  );
}

/**
 * A wrapper that scopes its subtree to one module, both in the DOM (the `[data-module-scope]` element
 * the CSS reads) and for the overlays opened from inside it (the context). For a part that carries
 * its own module wherever it sits: the People and Operations sub-nav bars.
 */
export function ModuleScope({ accent, children }: { accent: ModuleAccent; children: ReactNode }) {
  const scope = useMemo(() => moduleAccentScope(accent), [accent]);
  return (
    <ModuleScopeContext.Provider value={scope}>
      <div {...scope}>{children}</div>
    </ModuleScopeContext.Provider>
  );
}
